import { X } from "lucide-react";
import {
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";

import { cn } from "@/lib/utils";

const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

interface DrawerShellProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Small muted line under the title (mockup .drawer-sub). */
  sub?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function DrawerShell({ open, onClose, title, sub, children, className }: DrawerShellProps) {
  // Two-flag mount/show pattern: `mounted` keeps the panel in the DOM through
  // the exit transition; `shown` drives the enter/exit transform. The rAF hop
  // on open lets the browser paint the off-screen frame first, so the enter
  // slide actually animates instead of snapping.
  const [mounted, setMounted] = useState(open);
  const [shown, setShown] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  // Dialog focus contract: move focus into the panel on open, keep Tab
  // cycling inside it, hand focus back to the opener on close.
  useEffect(() => {
    if (open) {
      returnFocusRef.current =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
      const raf = requestAnimationFrame(() => panelRef.current?.focus());
      return () => cancelAnimationFrame(raf);
    }
    returnFocusRef.current?.focus();
    returnFocusRef.current = null;
    return undefined;
  }, [open]);

  const trapTab = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Tab" || !panelRef.current) {
      return;
    }
    const focusables = panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE);
    if (focusables.length === 0) {
      return;
    }
    const first = focusables[0]!;
    const last = focusables[focusables.length - 1]!;
    // Initial focus sits on the panel container itself — wrap from there too,
    // otherwise Shift+Tab escapes the dialog backwards.
    const onContainer = document.activeElement === panelRef.current;
    if (e.shiftKey && (onContainer || document.activeElement === first)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (onContainer || document.activeElement === last)) {
      e.preventDefault();
      first.focus();
    }
  };

  useEffect(() => {
    if (!open) {
      setShown(false);
      return undefined;
    }
    setMounted(true);
    // Double rAF: the first fires before the freshly-mounted panel's initial
    // off-screen frame is painted, so flipping the class there gets coalesced
    // into one style pass and the enter slide never animates. The second rAF
    // lands after that paint, giving the transition two distinct states.
    let inner: number;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setShown(true));
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!mounted) {
    return null;
  }

  return (
    <>
      <div
        aria-hidden="true"
        className={cn(
          "fixed inset-0 z-40 bg-black/5 transition-opacity duration-300",
          shown ? "opacity-100" : "opacity-0",
        )}
        onClick={onClose}
      />
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onKeyDown={trapTab}
        onTransitionEnd={(e) => {
          // Unmount only after the panel's own exit slide finishes.
          if (!open && e.target === e.currentTarget) {
            setMounted(false);
          }
        }}
        className={cn(
          "border-wg-03 fixed inset-y-0 right-0 z-50 flex w-[640px] max-w-[92%] flex-col overflow-y-auto border-l bg-white px-6 pt-6 pb-8 shadow-[-16px_0_40px_rgba(0,0,0,0.08)]",
          "transition-transform duration-300 ease-out",
          shown ? "translate-x-0" : "translate-x-full",
          className,
        )}
      >
        <div className="mb-5 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-heading text-[19px] font-medium text-black">{title}</h3>
            {sub != null && (
              <div className="text-wg-07 mt-0.5 text-[12.5px] tabular-nums">{sub}</div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex size-[34px] shrink-0 cursor-pointer items-center justify-center rounded-full bg-black text-white"
          >
            <X className="size-4" strokeWidth={2.4} />
          </button>
        </div>
        {children}
      </div>
    </>
  );
}
